import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useCallback } from "react";
import { observer } from "mobx-react";
import { Empty, Table, Tag, Tooltip, Result } from "antd";
import { __ } from "../../../../utils";
import { CheckCircleTwoTone, CloseCircleTwoTone, QuestionCircleOutlined } from "@ant-design/icons";
import { ScannerMarkupPopover } from ".";
import { useStores } from "../../../../store";
import { useScannerProgress } from "../../../../hooks";
const { Column } = Table;
const ScannerAllExternalUrlsTable = observer((param)=>{
    let { instance, style, reloadDependencies = [], reload = true, tableProps = {} } = param;
    const { scannerStore } = useStores();
    const { identifier, busy } = instance;
    const urls = scannerStore.resultAllExternalUrls.get(identifier);
    const getRowKey = useCallback((param)=>{
        let { data: { id } } = param;
        return id;
    }, []);
    const { remaining } = useScannerProgress();
    const [error, setError] = useState("");
    useEffect(()=>{
        async function doAsync() {
            if (reload) {
                try {
                    await scannerStore.fetchResultAllExternals(instance);
                    setError("");
                } catch (e) {
                    var _e_responseJSON;
                    setError(((_e_responseJSON = e.responseJSON) == null ? void 0 : _e_responseJSON.message) || e.message);
                }
            }
        }
        doAsync();
    }, [
        instance,
        reload,
        ...reloadDependencies
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            remaining > 0 && /*#__PURE__*/ _jsx("div", {
                className: "notice notice-info below-h2 notice-alt",
                style: {
                    margin: "0 0 10px"
                },
                children: /*#__PURE__*/ _jsx("p", {
                    children: __("Since the scanner is currently still running, the table may be refreshed.")
                })
            }),
            /*#__PURE__*/ _jsxs(Table, {
                locale: {
                    emptyText: error ? /*#__PURE__*/ _jsx(Result, {
                        title: __("Something went wrong."),
                        subTitle: error,
                        status: "500"
                    }) : /*#__PURE__*/ _jsx(Empty, {
                        description: __("No external URL found")
                    })
                },
                dataSource: Array.from(urls ? urls.values() : []),
                rowKey: getRowKey,
                size: "small",
                bordered: true,
                style: style,
                loading: !urls || urls.size === 0 ? busy : false,
                ...tableProps,
                children: [
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Last scanned"),
                        defaultSortOrder: "descend",
                        sorter: (a, b)=>new Date(a.data.lastScanned).getTime() - new Date(b.data.lastScanned).getTime(),
                        dataIndex: [
                            "data",
                            "lastScanned"
                        ],
                        render: (value, param)=>{
                            let { data: { lastScanned } } = param;
                            return new Date(lastScanned).toLocaleString(document.documentElement.lang);
                        }
                    }, "created"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("HTML Tag"),
                        dataIndex: [
                            "data",
                            "tag"
                        ],
                        render: (value, param)=>{
                            let { data: { tag } } = param;
                            return /*#__PURE__*/ _jsx(Tag, {
                                children: `<${tag} />`
                            });
                        }
                    }, "tag"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: /*#__PURE__*/ _jsx(Tooltip, {
                            title: __("This status shows you if the URL was blocked by a content blocker you defined."),
                            children: /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    __("Blocked?"),
                                    " ",
                                    /*#__PURE__*/ _jsx(QuestionCircleOutlined, {
                                        style: {
                                            color: "#9a9a9a"
                                        }
                                    })
                                ]
                            })
                        }),
                        sorter: (a, b)=>a.data.blocked === b.data.blocked ? 0 : a.data.blocked ? -1 : 1,
                        dataIndex: [
                            "data",
                            "blocked"
                        ],
                        render: (value, param)=>{
                            let { data: { blocked } } = param;
                            return blocked ? /*#__PURE__*/ _jsx(CheckCircleTwoTone, {
                                twoToneColor: "#52c41a"
                            }) : /*#__PURE__*/ _jsx(CloseCircleTwoTone, {
                                twoToneColor: "#eb2f96"
                            });
                        }
                    }, "blocked"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Blocked URL"),
                        dataIndex: [
                            "data",
                            "blockedUrl"
                        ],
                        render: (value, param)=>{
                            let { blockedUrlTruncate, data: { blockedUrl } } = param;
                            return /*#__PURE__*/ _jsx("a", {
                                href: blockedUrl,
                                target: "_blank",
                                rel: "noreferrer",
                                title: blockedUrl,
                                children: blockedUrlTruncate
                            });
                        }
                    }, "blockedUrl"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Found on this URL"),
                        sorter: (a, b)=>a.data.sourceUrl.localeCompare(b.data.sourceUrl),
                        dataIndex: [
                            "data",
                            "sourceUrl"
                        ],
                        render: (value, param)=>{
                            let { sourceUrlTruncate, data: { sourceUrl } } = param;
                            return /*#__PURE__*/ _jsx("a", {
                                href: sourceUrl,
                                target: "_blank",
                                rel: "noreferrer",
                                children: sourceUrlTruncate
                            });
                        }
                    }, "sourceUrl"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Actions"),
                        dataIndex: [
                            "data",
                            "markup"
                        ],
                        render: (value, record)=>record.data.markup ? /*#__PURE__*/ _jsx(ScannerMarkupPopover, {
                                record: record
                            }) : ""
                    }, "markup")
                ]
            })
        ]
    });
});
export { ScannerAllExternalUrlsTable };
